/* eslint-disable no-shadow */
import * as ModelEvent from "@node-elion/syncron";

import SubscriptionPool from "../../redux/services/SubscriptionPool";
import createLogger from "../../utils/logger.util";
import OptionsBase from "../../types/ServiceSubscribeOptionsBase";
import Subscription from "../../types/Subscription";
import {
	NonEditableProperties,
	NonEditablePropertyNames,
} from "../../types/NonEditableProperties";
import { ModelId } from "../../types/ModelId";
import { BlacklistPhoneLevel } from "../../pages/MainPage/pages/Blacklist/tabs/Customers/components/Modal/components/Content/tabs/Main/components/Level/constants";
import { Base, Company } from "..";
import { getPRPC } from "../../hooks/usePRPC";
import createRPCQuery from "../../utils/createRPCQuery.util";

const logger = createLogger({ name: "CustomersBlacklist" });

class CustomersBlacklist extends Base {
	public static fromResponse(data: any): CustomersBlacklist.Model {
		const companies = data.blacklistPhoneToCompanies.flatMap((item) =>
			Company.fromResponse(item.company),
		);

		return {
			id: data.id,

			companies,
			companyIds: companies.map(({ id }) => id),
			phone: data.phone,
			level: data.level,
			description: data.description,
			surname: data.surname,
			name: data.name,
			fatherName: data.fatherName,

			createdAt: data.createdAt,
			updatedAt: data.updatedAt,
			deletedAt: data.deletedAt,
		};
	}

	public static toRequest(
		model: CustomersBlacklist.New | CustomersBlacklist.Modified,
	) {
		const payload = {
			phone: model.phone || "",
			level: model.level,
			description: model.description || "",
			surname: model.surname || "",
			name: model.name || "",
			fatherName: model.fatherName || "",
			companyIds: model.companyIds,
		};

		logger.info("toRequest", { payload, model });
		return payload;
	}

	public static async getAll() {
		const res = await this.request((prpc) =>
			prpc.theirsModel.blacklistPhone.getAll(),
		);
		return res?.items?.map(this.fromResponse);
	}

	public static async store(object: CustomersBlacklist.New) {
		try {
			await this.request((prpc) =>
				prpc.theirsModel.blacklistPhone.create(this.toRequest(object)),
			);
		} catch (err: any) {
			return false;
		}
		return true;
	}

	public static async update(object: CustomersBlacklist.Modified) {
		try {
			await this.request((prpc) =>
				prpc.theirsModel.blacklistPhone.update(
					object.id,
					this.toRequest(object),
				),
			);
		} catch (err: any) {
			return false;
		}
		return true;
	}

	public static async delete(id: number[] | number) {
		const p = this.request((prpc) =>
			prpc.theirsModel.blacklistPhone.delete(id),
		);
		if (!p) {
			return;
		}
		await p;
	}

	static async startImport(payload: {
		items?: CustomersBlacklist.ImportItem[];
		totalRows: number;
		companyIds: number[];
		level: BlacklistPhoneLevel;
		isLast?: boolean;
	}) {
		const prpc = getPRPC();
		if (!prpc) {
			logger.error(
				"[CustomersBlacklist] startImport: PRPC is not available",
			);
			throw new Error(
				"[CustomersBlacklist] WebSocket session is not ready (PRPC missing)",
			);
		}
		const model = prpc.theirsModel.blacklistPhone as typeof prpc.theirsModel.blacklistPhone & {
			startImport: (
				p: typeof payload,
			) => Promise<CustomersBlacklist.ImportProgress | null>;
		};
		return createRPCQuery(() => model.startImport(payload), {
			silent: false,
		});
	}

	static async appendImportChunk(payload: {
		items: CustomersBlacklist.ImportItem[];
		isLast?: boolean;
	}) {
		const prpc = getPRPC();
		if (!prpc) {
			logger.error(
				"[CustomersBlacklist] appendImportChunk: PRPC is not available",
			);
			throw new Error(
				"[CustomersBlacklist] WebSocket session is not ready (PRPC missing)",
			);
		}
		const model = prpc.theirsModel.blacklistPhone as typeof prpc.theirsModel.blacklistPhone & {
			appendImportChunk: (
				p: typeof payload,
			) => Promise<CustomersBlacklist.ImportProgress | null>;
		};
		return createRPCQuery(() => model.appendImportChunk(payload), {
			silent: false,
		});
	}

	static async getImportProgress() {
		const prpc = getPRPC();
		if (!prpc) return null;
		const model = prpc.theirsModel.blacklistPhone as typeof prpc.theirsModel.blacklistPhone & {
			getImportProgress: () => Promise<
				CustomersBlacklist.ImportProgress | null
			>;
		};
		return createRPCQuery(() => model.getImportProgress());
	}

	static async cancelImport() {
		const prpc = getPRPC();
		if (!prpc) return null;
		const model = prpc.theirsModel.blacklistPhone as typeof prpc.theirsModel.blacklistPhone & {
			cancelImport: () => Promise<
				CustomersBlacklist.ImportProgress | null
			>;
		};
		return createRPCQuery(() => model.cancelImport());
	}

	public static async subscribe(
		options: CustomersBlacklist.SubscribeOptions,
		onUpdate: Subscription.OnUpdate<CustomersBlacklist.Model>,
	): Promise<Subscription<CustomersBlacklist.SubscribeOptions> | null> {
		const modelEventConstructor = new ModelEvent.ModelEventConstructor({
			onUpdate: (state) => {
				onUpdate({
					...state,
					models: state.models.map(this.fromResponse),
				});
			},
		});
		const subscription = await SubscriptionPool.add(
			(prpc) =>
				prpc.theirsModel.blacklistPhone.subscribe({
					params: options,
					ping: () => true,
					onEvent: async (events) => {
						await modelEventConstructor.onEvent(events);
					},
					onError: (error) => {
						logger.error(error);
					},
				}),
			{ name: "CustomersBlacklist.subscribe" },
		);

		return {
			unsubscribe: () => subscription.unsubscribe(),
			update: (options: CustomersBlacklist.SubscribeOptions) =>
				subscription.update(options),
		};
	}
}

declare namespace CustomersBlacklist {
	interface Model extends NonEditableProperties {
		companies?: Company.Model[];
		companyIds: ModelId[];
		phone: string;
		level: BlacklistPhoneLevel;
		description: string;
		surname: string;
		name: string;
		fatherName?: string;
	}

	type New = Omit<Model, NonEditablePropertyNames>;
	type Modified = Partial<Model> & Pick<NonEditableProperties, "id">;

	interface SubscribeOptions extends OptionsBase<CustomersBlacklist.Model> {
		companyIds?: ModelId[];
	}

	interface ImportItem {
		phone: string;
		description: string;
		name?: string;
		surname?: string;
		fatherName?: string;
	}

	type ImportProgressStatus =
		| "running"
		| "completed"
		| "failed"
		| "cancelled";

	interface ImportProgress {
		status: ImportProgressStatus;
		totalRows: number;
		processedRows: number;
		createdCount: number;
		brokenCount: number;
		skippedDuplicateCount: number;
		failedCount: number;
		errors: { index: number; message: string }[];
		startedAt: string;
		updatedAt: string;
		finishedAt?: string;
	}
}

export default CustomersBlacklist;
